import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalBody, ModalFooter, ModalHeader, Modal, Button } from 'reactstrap';
import { IconButton, Tooltip, Typography } from '@mui/material';
import { AlternateEmail } from '@material-ui/icons';
import qs from 'qs';
import oikosApi from '../../../services/oikos';
import { notifyError } from '../../../utils/notify';
import DisparoEmailsCotacaoForm from './DisparoEmailsCotacaoForm';

const EMAIL_ENVIADO_PRIMARY = '#007bff';

const ModalFormDisparoEmails = (props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [emailEnvios, setEmailEnvios] = useState([]);
  const formRef = useRef(null);

  const pedidoCompraId = Number(props.pedidoCompraData?.pedidoCompra_id) || 0;

  const fetchEmailEnvios = useCallback(async () => {
    if (!pedidoCompraId) {
      setEmailEnvios([]);
      return;
    }

    try {
      const response = await oikosApi.get(`/?${qs.stringify({
        action: 'get',
        table: 'emailEnvio',
        data: {
          emailEnvio_tabRef: 'pedidoCompra',
          emailEnvio_tabId: pedidoCompraId,
          emailEnvio_status: 'enviado',
        },
      })}`);
      setEmailEnvios(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar emailEnvio do pedidoCompra:', error);
      setEmailEnvios([]);
    }
  }, [pedidoCompraId]);

  useEffect(() => {
    fetchEmailEnvios();
  }, [fetchEmailEnvios]);

  const emailCount = emailEnvios.length;
  const hasEmailsEnviados = emailCount > 0;

  const destinatarios = useMemo(() => (
    emailEnvios
      .map((row) => String(row.emailEnvio_destinatarioEmail || '').trim())
      .filter(Boolean)
  ), [emailEnvios]);

  const tooltipTitle = useMemo(() => {
    if (!hasEmailsEnviados) {
      return 'Disparo de emails para cotação';
    }

    const uniqueEmails = [...new Set(destinatarios)];
    return (
      <div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          {emailCount} {emailCount === 1 ? 'e-mail enviado' : 'e-mails enviados'}
        </div>
        {uniqueEmails.map((email) => (
          <div key={email}>{email}</div>
        ))}
      </div>
    );
  }, [destinatarios, emailCount, hasEmailsEnviados]);

  const handleClickOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleClickCloseModal = () => {
    setIsModalOpen(false);
    formRef.current?.reset?.();
  };

  const handleSendEmails = async () => {
    if (!formRef.current?.hasEmails?.()) {
      notifyError('Adicione ao menos um email para envio.');
      return;
    }
    const sent = await formRef.current.sendEmails();
    if (sent) {
      await fetchEmailEnvios();
      props.onEmailsSent?.();
      handleClickCloseModal();
    }
  };

  return (
    <>
      <Tooltip title={tooltipTitle} arrow placement="top">
        <span className="d-inline-flex align-items-center" style={{ gap: 2 }}>
          <IconButton
            onClick={handleClickOpenModal}
            size="small"
            aria-label={hasEmailsEnviados
              ? `${emailCount} e-mails enviados`
              : 'Disparo de emails para cotação'}
            style={hasEmailsEnviados ? { color: EMAIL_ENVIADO_PRIMARY } : undefined}
          >
            <AlternateEmail fontSize="small" />
          </IconButton>
          {hasEmailsEnviados ? (
            <span
              style={{
                color: EMAIL_ENVIADO_PRIMARY,
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1,
                minWidth: 12,
              }}
            >
              {emailCount}
            </span>
          ) : null}
        </span>
      </Tooltip>
      <Modal
        isOpen={isModalOpen}
        toggle={handleClickCloseModal}
        size="lg"
      >
        <ModalHeader>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Disparo de emails para cotação - {props.pedidoCompraData?.pedidoCompra_desc}
          </Typography>
        </ModalHeader>
        <ModalBody>
          {isModalOpen ? (
            <DisparoEmailsCotacaoForm
              ref={formRef}
              pedidoCompraData={props.pedidoCompraData}
              fetchPedidoCompraDocuments={props.fetchPedidoCompraDocuments}
            />
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={handleSendEmails}>Enviar emails</Button>
          <Button onClick={handleClickCloseModal}>Voltar</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ModalFormDisparoEmails;
